import {
  booleanAttribute,
  Component, ComponentRef,
  effect,
  ElementRef,
  inject,
  Input,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  output,
  ViewChild,
  OnChanges,
  Type,
} from '@angular/core';
import {
  ConnectedPosition,
  Overlay,
  OverlayModule,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { Feature, Map as OlMap } from 'ol';
import { Geometry } from 'ol/geom';
import { CollectionEvent } from 'ol/Collection';
import View from 'ol/View';
import { Attribution, ScaleLine } from 'ol/control';
import MultiPolygon from 'ol/geom/MultiPolygon';
import { Layer } from 'ol/layer';
import BaseLayer from 'ol/layer/Base';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import { transform as transformCoordinates } from 'ol/proj';
import { OSM } from 'ol/source';
import TileWMS from 'ol/source/TileWMS';
import VectorSource from 'ol/source/Vector';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { filter, take } from 'rxjs';
import { GenPopover } from '../../../generated/serverModels/GenPopover';
import { GenServiceType } from '../../../generated/serverModels/GenServiceType';
import { AppService } from '../../../services/app.service';
import { LayerService } from '../../../services/layer.service';
import { MapService } from '../../../services/map.service';
import { MapLayer } from '../../classes/map-layer';
import { PopoverData } from '../../classes/popover-data';
import { LayerPopupComponent } from '../layer-popup/layer-popup.component';

@Component({
  selector: 'tgrid-map',
  templateUrl: './map.component.html',
  imports: [
    ReactiveFormsModule,
    OverlayModule,
  ],
  standalone: true,
  styleUrl: './map.component.scss',
})
export class MapComponent implements OnInit, OnDestroy, OnChanges {
  private readonly BASELAYER_NAME = 'OSM Baselayer';
  private readonly POPOVER_OFFSET = 5;

  @ViewChild('mapTarget', { static: true }) mapElement!: ElementRef<HTMLDivElement>;
  @Input({ transform: booleanAttribute }) hideAppState = false;


  public mapId = input<string>('tgridBaseMap');
  public hasPopovers = input<boolean>(true);

  public layerAdded = output<BaseLayer>();
  public layerRemoved = output<BaseLayer>();


  private mapService = inject(MapService);
  private layerService = inject(LayerService);
  private appService = inject(AppService);

  private ngZone = inject(NgZone);
  private readonly cdkOverlay = inject(Overlay);

  public map!: OlMap;

  private popupOverlayRef?: OverlayRef;

  private stateBoundaryLayer = new VectorLayer({
    source: new VectorSource<Feature<Geometry>>({
      features: [],
    }),
    opacity: 1,
    style: () => {
      return new Style({
        stroke: new Stroke({
          color: 'black',
          width: 2,
        }),
      });
    },
  });


  constructor() {
    this.mapService.activeIdentifyLayers.pipe(
      filter(layers => layers.length === 0 && !!this.popupOverlayRef?.hasAttached()),
      takeUntilDestroyed(),
    ).subscribe(() => this.hidePopup());

    effect(() => {
      const states = this.appService.selectedStates();
      this.stateBoundaryLayer.getSource()?.clear();
      states.forEach(state => {
        const boundary = JSON.parse(state.boundary);
        this.stateBoundaryLayer.getSource()?.addFeature(
          new Feature(new MultiPolygon(boundary.coordinates)),
        );
      });

      if (this.map) {
        const extent = this.stateBoundaryLayer.getSource()!.getExtent();
        const isExtentValid = extent.every(coordinate => !isNaN(coordinate) && isFinite(coordinate));
        if (isExtentValid) {
          this.map.getView().fit(this.stateBoundaryLayer.getSource()!.getExtent());
        }
      }
    });
  }


  ngOnInit(): void {
    // Using runOutsideAngular to not trigger change detection on every pointer move
    const layers: Layer[] = [
      new TileLayer({
        source: new OSM(),
        properties: {
          name: this.BASELAYER_NAME,
        },
      })];
    if (!this.hideAppState) {
      layers.push(this.stateBoundaryLayer);
    }
    this.map = new OlMap({
      layers,
      target: this.mapElement.nativeElement,
      controls: [
        new ScaleLine({ units: 'us' }),
        new Attribution(),
      ],
      view: new View({
        center: transformCoordinates([-98.5795, 39.8282], 'EPSG:4326', 'EPSG:3857'),
        zoom: 4,
        maxZoom: 18,
      }),
    });

    const extent = this.stateBoundaryLayer.getSource()!.getExtent();
    const isExtentValid = extent.every(coordinate => !isNaN(coordinate) && isFinite(coordinate));
    if (isExtentValid) {
      this.map.getView().fit(extent);
    }
    MapService.mapRefs[this.mapId()] = this.map;

    if (this.hasPopovers()) {
      this.initializePopover();
    }

    // @ts-expect-error: OpenLayers typings don't include this event union type
    this.map.getLayers().on(['add', 'remove'], (event: CollectionEvent<BaseLayer>) => {

      if (event.type === 'add') {
        this.layerAdded.emit(event.element);
        return;
      }

      this.layerRemoved.emit(event.element);
    });


    this.ngZone.runOutsideAngular(() => {
      this.map.on('pointermove', (event) => {
        if (this.mapService.pointSelectionActive) return;
        this.map.getTargetElement().style.cursor = (this.map.hasFeatureAtPixel(event.pixel)) ? 'pointer' : '';
      });
    });
  }

  private initializePopover(): void {
    this.map.on('singleclick', (event) => {
      this.hidePopup();

      if (this.mapService.pointSelectionActive) {
        this.mapService.goToPoint$.next({ mapId: this.mapId(), coordinate: event.coordinate });
        return;
      }

      const activeIdentifyLayers: MapLayer[] = this.mapService.activeIdentifyLayers.value;
      if (!this.map.hasFeatureAtPixel(event.pixel) && activeIdentifyLayers.length === 0) {
        return;
      }

      let popupComponent: ComponentRef<LayerPopupComponent>;

      if (this.map.hasFeatureAtPixel(event.pixel)) {
        const topFeature = this.map.getFeaturesAtPixel(event.pixel)[0];
        if (topFeature.getProperties()['popoverComponent']) {
          popupComponent = this.attachPopupComponent(topFeature.getProperties()['popoverComponent']);
          popupComponent.setInput('data', topFeature.getProperties());

        } else {
          popupComponent = this.attachPopupComponent(LayerPopupComponent);

          const v = new Map<string, PopoverData[]>();
          this.map.forEachFeatureAtPixel(event.pixel, (feature, layer) => {
            const popoverData = (layer.get('type') === GenServiceType.VECTORTILE) ? layer.get('popoverFunction')(feature.getProperties())
              : feature.getProperties()['popoverData'];
            const layerName = layer.get('name');
            v.set(layerName, popoverData);
          });
          popupComponent.instance.subjectData.next(v);

        }
      }

      if (activeIdentifyLayers.length !== 0) {
        popupComponent = this.attachPopupComponent(LayerPopupComponent);
        activeIdentifyLayers.forEach(layer => {

          const url = (layer.renderedLayer as TileLayer<TileWMS>).getSource()?.getFeatureInfoUrl(event.coordinate, this.map.getView().getResolution() as number, 'EPSG:3857', {
            'INFO_FORMAT': 'application/json',
            'FEATURE_COUNT': '1000',
          });
          this.layerService.getGeoserverFeatureAtCoordinate(url as string).pipe(
            take(1),
          )
            .subscribe(rawData => {
              const data = rawData as {features: {properties: Record<string, unknown>}[]};
              if (data.features.length === 0) {
                this.hidePopup();
                return;
              }

              const pd = data.features.flatMap((f: {properties: Record<string, unknown>}) => {
                if (layer.uiHandle.startsWith('icf')) {

                  const existingIndex = layer.popoverData.findIndex(p => p.ordering === 900);

                  if (existingIndex !== -1) {
                    layer.popoverData.splice(existingIndex, 1);
                  }

                  const currentProp = new GenPopover({
                    displayText: (layer.selectedAttribute as {displayName: string}).displayName,
                    propertyName: (layer.selectedAttribute as {dbColumn: string}).dbColumn,
                    valueFormat: '1.0-0',
                    ordering: 900,
                  });
                  layer.popoverData.push(currentProp);
                }


                return layer.createPopover(f.properties);

              });


              popupComponent.instance.subjectData.next(new Map<string, PopoverData[]>([[layer.displayName, pd]]));
            });

        });
      }

      this.showPopup(event.coordinate);
    });
  }

  private attachPopupComponent(componentType: Type<LayerPopupComponent>): ComponentRef<LayerPopupComponent> {
    const overlayRef = this.getOrCreatePopupOverlay();
    overlayRef.detach();
    return overlayRef.attach(new ComponentPortal(componentType));
  }

  private getOrCreatePopupOverlay(): OverlayRef {
    if (this.popupOverlayRef) {
      return this.popupOverlayRef;
    }

    const initialStrategy = this.cdkOverlay.position()
      .flexibleConnectedTo({ x: 0, y: 0 })
      .withPositions(this.getDefaultPositions())
      .withPush(true);

    this.popupOverlayRef = this.cdkOverlay.create({
      positionStrategy: initialStrategy,
      scrollStrategy: this.cdkOverlay.scrollStrategies.reposition(),
      panelClass: 'tgrid-cdk-map-overlay',
      hasBackdrop: false,
    });

    return this.popupOverlayRef;
  }

  private getDefaultPositions(): ConnectedPosition[] {
    return [
      { originX: 'center', originY: 'center', overlayX: 'start', overlayY: 'top', offsetX: this.POPOVER_OFFSET, offsetY: this.POPOVER_OFFSET },
      { originX: 'center', originY: 'center', overlayX: 'end', overlayY: 'top', offsetX: -this.POPOVER_OFFSET, offsetY: this.POPOVER_OFFSET },
      { originX: 'center', originY: 'center', overlayX: 'start', overlayY: 'bottom', offsetX: this.POPOVER_OFFSET, offsetY: -this.POPOVER_OFFSET },
      { originX: 'center', originY: 'center', overlayX: 'end', overlayY: 'bottom', offsetX: -this.POPOVER_OFFSET, offsetY: -this.POPOVER_OFFSET },
    ];
  }

  private getPositionsForPixel(pixel: [number, number], mapSize: [number, number]): ConnectedPosition[] {
    const [pixelX, pixelY] = pixel;
    const [mapWidth, mapHeight] = mapSize;
    const isTop = pixelY <= mapHeight / 2;
    const isLeft = pixelX <= mapWidth / 2;

    const preferredPosition: ConnectedPosition = isTop && isLeft
      ? { originX: 'center', originY: 'center', overlayX: 'start', overlayY: 'top', offsetX: this.POPOVER_OFFSET, offsetY: this.POPOVER_OFFSET }
      : isTop && !isLeft
        ? { originX: 'center', originY: 'center', overlayX: 'end', overlayY: 'top', offsetX: -this.POPOVER_OFFSET, offsetY: this.POPOVER_OFFSET }
        : !isTop && isLeft
          ? { originX: 'center', originY: 'center', overlayX: 'start', overlayY: 'bottom', offsetX: this.POPOVER_OFFSET, offsetY: -this.POPOVER_OFFSET }
          : { originX: 'center', originY: 'center', overlayX: 'end', overlayY: 'bottom', offsetX: -this.POPOVER_OFFSET, offsetY: -this.POPOVER_OFFSET };

    const defaults = this.getDefaultPositions();
    return [
      preferredPosition,
      ...defaults.filter(position =>
        position.overlayX !== preferredPosition.overlayX || position.overlayY !== preferredPosition.overlayY,
      ),
    ];
  }

  // TODO: Move to service
  public showPopup(coordinate: number[]): void {
    const pixel = this.map.getPixelFromCoordinate(coordinate) as [number, number];
    const mapSize: [number, number] = this.map.getSize() as [number, number];
    const mapRect = this.mapElement.nativeElement.getBoundingClientRect();
    const screenPoint = {
      x: Math.round(mapRect.left + pixel[0]),
      y: Math.round(mapRect.top + pixel[1]),
    };

    const positionStrategy = this.cdkOverlay.position()
      .flexibleConnectedTo(screenPoint)
      .withPositions(this.getPositionsForPixel(pixel, mapSize))
      .withPush(true);

    const overlayRef = this.getOrCreatePopupOverlay();
    overlayRef.updatePositionStrategy(positionStrategy);
    overlayRef.updatePosition();
  }

  // TODO: Move to service
  public hidePopup(): void {
    this.popupOverlayRef?.detach();
  }

  ngOnDestroy() {
    this.popupOverlayRef?.dispose();
    delete MapService.mapRefs[this.mapId()];
  }

  ngOnChanges() {
    if (!this.map) return;
    if (this.hideAppState) {
      this.map.removeLayer(this.stateBoundaryLayer);
    } else if (this.map && !this.map.getLayers().getArray().includes(this.stateBoundaryLayer)) {
      this.map.addLayer(this.stateBoundaryLayer);
    }
  }

}
